import React from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import css from './PullTrack.module.scss';

export type StationTone = 'origin' | 'opportunities' | 'replies' | 'process';

export interface StationProps {
  tone: StationTone;
  label: string;
  value: number;
  sublabel?: string;
  footnote?: string;
  tooltip?: string;
  focused?: boolean;
  /** Rail dot greys out when the stage hasn't been reached yet (set by PullTrack). */
  dimmed?: boolean;
  to: string;
  onSelect?: () => void;
}

const DOT: Record<StationTone, string> = {
  origin: css.dotOrigin,
  opportunities: css.dotOpportunities,
  replies: css.dotReplies,
  process: css.dotProcess,
};

const fmt = (n: number) => n.toLocaleString();

/** One generic station on the pull track: a rail dot plus its count + label. */
const PullStation: React.FC<StationProps> = ({
  tone,
  label,
  value,
  sublabel,
  footnote,
  tooltip,
  focused,
  dimmed,
  to,
  onSelect,
}) => {
  // Fold the tooltip caveat into the accessible name so screen-reader users get
  // the same nuance sighted users get on hover.
  const ariaLabel = `${label}: ${fmt(value)}${sublabel ? ` ${sublabel}` : ''}${tooltip ? `. ${tooltip}` : ''}`;
  const link = (
    <Link to={to} className={css.main} onClick={onSelect} aria-label={ariaLabel}>
      <span className={css.value}>{fmt(value)}</span>
      <span className={css.label}>{label}</span>
      {sublabel && <span className={css.sublabel}>{sublabel}</span>}
    </Link>
  );

  return (
    <div className={`${css.station} ${focused ? css.focused : ''}`}>
      <div className={css.nodeRow}>
        <span className={`${css.dot} ${dimmed ? '' : DOT[tone]}`} aria-hidden="true" />
      </div>
      <div className={css.body}>
        {tooltip ? <Tooltip title={tooltip}>{link}</Tooltip> : link}
        {footnote && <span className={css.footnote}>{footnote}</span>}
      </div>
    </div>
  );
};

export default PullStation;
